import type { FRAYER_MODEL_GENERATOR_STRINGS } from "@/components/tools/frayer-model-generator";
import { t } from "@/lib/i18n/interpolate";
import {
  type Model,
  modelLines,
  type Square,
} from "@/lib/tool-kit/games-organizers/frayer";

type Strings = typeof FRAYER_MODEL_GENERATOR_STRINGS;

const SQUARE_BORDERS: Record<Square, string> = {
  definition: "border-r border-b",
  characteristics: "border-b",
  examples: "border-r",
  nonExamples: "",
};

const RULED_LINES = 14;

function Ruled() {
  return (
    <div aria-hidden="true" className="flex-1 overflow-hidden">
      {Array.from({ length: RULED_LINES }, (_, i) => (
        <div
          className="h-8 border-neutral-400 border-b"
          // biome-ignore lint/suspicious/noArrayIndexKey: identical blank lines
          key={i}
        />
      ))}
    </div>
  );
}

function SquareBox({
  square,
  heading,
  lines,
  ruled,
}: {
  square: Square;
  heading: string;
  lines: string[];
  ruled: boolean;
}) {
  return (
    <div
      className={`flex min-h-0 flex-col gap-1 border-neutral-800 p-3 ${SQUARE_BORDERS[square]}`}
    >
      <p className="font-bold text-sm uppercase tracking-wide">{heading}</p>
      {lines.length > 0 ? (
        <ul className="m-0 list-disc space-y-1 pl-5 text-sm">
          {lines.map((line, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: lines may repeat
            <li key={i}>{line}</li>
          ))}
        </ul>
      ) : null}
      {lines.length === 0 && ruled ? <Ruled /> : null}
    </div>
  );
}

/** One Frayer organizer: the term in an oval over the meeting point of four squares. */
export default function FrayerSheet({
  term,
  model,
  headings,
  ruled,
  nameLine,
  ratio,
  strings,
}: {
  term: string;
  model: Model | null;
  headings: Record<Square, string>;
  ruled: boolean;
  nameLine: boolean;
  ratio: number;
  strings: Strings;
}) {
  const squares = [
    "definition",
    "characteristics",
    "examples",
    "nonExamples",
  ] as const;
  return (
    <section
      aria-label={t(strings.sheetLabel, { term })}
      className="ws-sheet mx-auto w-full break-after-page rounded-sm border border-border bg-white p-4 text-neutral-900 shadow-sm last:break-after-auto sm:p-8"
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-x-8 gap-y-2">
        <p className="font-semibold text-lg">{strings.sheetTitle}</p>
        {nameLine ? (
          <div className="flex gap-6 text-sm">
            <span className="flex items-end gap-2">
              {strings.name}
              <span className="inline-block w-48 border-neutral-900 border-b" />
            </span>
            <span className="flex items-end gap-2">
              {strings.date}
              <span className="inline-block w-28 border-neutral-900 border-b" />
            </span>
          </div>
        ) : null}
      </div>
      <div
        className="relative grid grid-cols-2 grid-rows-2 border-2 border-neutral-800"
        style={{ aspectRatio: ratio }}
      >
        {squares.map((square) => (
          <SquareBox
            heading={headings[square]}
            key={square}
            lines={model ? modelLines(model[square]) : []}
            ruled={ruled}
            square={square}
          />
        ))}
        <div className="absolute top-1/2 left-1/2 flex max-w-[44%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[50%] border-2 border-neutral-800 bg-white px-6 py-4 text-center font-bold text-lg leading-tight sm:text-2xl">
          {term}
        </div>
      </div>
    </section>
  );
}
